import type {LoginForm, OtpForm, PinLoginForm} from "@/stores/user"
import { useUserStore } from "@/stores/user"
import type { AxiosResponse } from "@/plugins/axios"
import type { App } from "vue"
import {requestPermissions} from "@/plugins/push"
import {roles} from "@/enums/roles"

export interface Auth {
  user: App.Models.AuthUser | null
  hasRole: (role: string) => boolean
  sendOtp: (form: OtpForm) => Promise<AxiosResponse<{ code?: string }>>
  login: (form: LoginForm) => Promise<AxiosResponse<{ access_token: string }>>
  pinLogin: (form: PinLoginForm) => Promise<AxiosResponse<{ access_token: string }>>
  loadUser: (force?: boolean) => Promise<App.Models.AuthUser>
  logout: () => Promise<void>
  loggedIn: boolean
  impersonate: (userId: number) => Promise<AxiosResponse<{ access_token: string }>>
  stopImpersonating: () => Promise<AxiosResponse<{ access_token: string }>>
  roleAtLeast: (role: string, requestingUser?: App.Models.AuthUser | null) => boolean
}

export const $auth: Auth = {
  get user(): App.Models.AuthUser | null {
    const userStore = useUserStore()
    return userStore.user
  },
  set user(user: App.Models.AuthUser | null) {
    const userStore = useUserStore()
    userStore.user = user
  },
  hasRole(role: string): boolean {
    return this.user?.role === role
  },
  sendOtp(form: OtpForm) {
    const userStore = useUserStore()
    return userStore.sendOtp(form)
  },

  async login(form: LoginForm) {
    const userStore = useUserStore()
    const response = await userStore.login(form)
    requestPermissions()
    return response
  },

  async pinLogin(form: PinLoginForm) {
    const userStore = useUserStore()
    const response = await userStore.pinLogin(form)
    requestPermissions()
    return response
  },
  async loadUser(force: boolean = false) {
    const userStore = useUserStore()
    const user = await userStore.loadUser(force)
    if(user) {
      requestPermissions()
    }
    return user
  },
  logout() {
    const userStore = useUserStore()
    return userStore.logout()
  },
  get loggedIn() {
    return !!this.user
  },
  async impersonate(userId: number) {
    const userStore = useUserStore()
    return await userStore.impersonate(userId)
  },
  async stopImpersonating() {
    const userStore = useUserStore()
    return await userStore.stopImpersonating()
  },
  roleAtLeast(role: string): boolean {
    if (!this.user) {
      return false
    }

    const rolesHierarchy = {
      [roles.LIBRA_ADMIN]: 0,
      [roles.ACCOUNT_OWNER]: 1,
      [roles.AUTONOMOUS]: 2,
      [roles.NON_AUTONOMOUS]: 3,
    }

    return rolesHierarchy[this.user.role] <= rolesHierarchy[role]
  },
}

export default {
  install(app: App) {
    app.config.globalProperties.$auth = $auth
  }
}
